import { Circle, Rect, Line, Triangle, Textbox } from 'fabric'
import { BLEED, MARGIN, SPINE_MARGIN, WIDTH_WITH_BLEED_PX } from '../constants'

// TODO: Add usememo to these
// Get coordinates for adding a new object.
const initialObjectPosition = (canvas, canvasName) => {
  const position = {}

  if (canvasName === 'spine') {
    position.top = SPINE_MARGIN + BLEED + 10
    position.left = SPINE_MARGIN + 10
  } else {
    position.top = MARGIN + BLEED + 20

    if (canvasName === 'front') {
      position.left = MARGIN + 20
    } else {
      position.left = MARGIN + BLEED + 20
    }
  }

  return position
}

// Provide canvas tools for creating objects.
export function useCanvasObjectCreators(canvas, currentCanvasName) {
  const position = initialObjectPosition(canvas, currentCanvasName)

  const addCircle = () => {
    const options = {
      radius: 72,
      fill: 'transparent',
      stroke: 'black',
      strokeUniform: true,
      strokeWidth: 3,
      noScaleCache: false,
    }

    const circle = new Circle({ ...options, ...position })

    canvas.add(circle)
  }

  const addRectangle = () => {
    const options = {
      width: 200,
      height: 100,
      fill: 'transparent',
      stroke: 'black',
      strokeUniform: true,
      strokeWidth: 3,
      noScaleCache: false,
    }
    let rectangle = new Rect({ ...options, ...position })

    canvas.add(rectangle)
  }

  const addLine = () => {
    const options = {
      stroke: 'black',
      strokeUniform: true,
      strokeWidth: 3,
      noScaleCache: false,
    }

    let line = new Line([50, 10, 200, 150], { ...options, ...position })
    canvas.add(line)
  }

  const addTriangle = () => {
    const options = {
      width: 150,
      height: 150,
      fill: 'transparent',
      stroke: 'black',
      strokeUniform: true,
      strokeWidth: 3,
      cornerColor: 'blue',
      noScaleCache: false,
    }

    const triangle = new Triangle({ ...options, ...position })
    canvas.add(triangle)
  }

  const addText = () => {
    const text = 'YourTextHere'

    const options = {
      fontSize: 96,
      stroke: 'black',
      fill: 'black',
      textAlign: 'center',
    }

    const tb = new Textbox(text, { ...options, ...position })

    // Prevent text from being stretched vertically.
    tb.setControlsVisibility({
      mt: false,
      mb: false,
    })

    canvas.add(tb)
  }

  const addTextVerticallyTopToBottom = () => {
    const text = 'YourTextHere'
    const tb = new Textbox(text, {
      fontSize: 96,
      stroke: 'black',
      fill: 'black',
      angle: 90,
      originX: 'left',
      originY: 'bottom',
      textAlign: 'center',
    })

    tb.setControlsVisibility({
      ml: false,
      mr: false,
    })

    canvas.add(tb)
    canvas.centerObject(tb)
  }

  const addTextVerticallyBottomToTop = () => {
    const text = 'YourTextHere'
    const tb = new Textbox(text, {
      fontSize: 96,
      stroke: 'black',
      fill: 'black',
      angle: 270,
      originX: 'right',
      originY: 'top',
      textAlign: 'center',
    })

    tb.setControlsVisibility({
      ml: false,
      mr: false,
    })

    canvas.add(tb)
    canvas.centerObject(tb)
  }

  return {
    addCircle,
    addLine,
    addRectangle,
    addText,
    addTextVerticallyTopToBottom,
    addTextVerticallyBottomToTop,
    addTriangle,
  }
}

// Callbacks for tools specifically for text.
export function useCanvasTextTools(
  canvas,
  textObject,
  pushHistory,
  setfontFamilyValue,
) {
  const textFontFamily = (value) => {
    setfontFamilyValue(value.target.value)

    textObject.set({
      fontFamily: value.target.value,
    })
    canvas.renderAll()
    pushHistory(canvas)
  }

  const textBold = () => {
    if (textObject.fontWeight !== 'bold') {
      textObject.set({
        fontWeight: 'bold',
      })
    } else {
      textObject.set({
        fontWeight: 'normal',
      })
    }
    canvas.renderAll()
    pushHistory(canvas)
  }

  const textUnderline = () => {
    textObject.set({
      underline: !textObject.underline,
    })
    canvas.renderAll()
    pushHistory(canvas)
  }

  const textStrikeThrough = () => {
    textObject.set({
      overline: !textObject.overline,
    })
    canvas.renderAll()
    pushHistory(canvas)
  }

  const textItalic = () => {
    if (textObject.fontStyle !== 'italic') {
      textObject.set({
        fontStyle: 'italic',
      })
    } else if (textObject.fontStyle === 'italic') {
      textObject.set({
        fontStyle: 'normal',
      })
    }
    canvas.renderAll()
    pushHistory(canvas)
  }

  // Justify text left
  const textLeft = () => {
    textObject.set({
      textAlign: 'left',
    })
    canvas.renderAll()
    pushHistory(canvas)
  }

  // Justify text center
  const textCenter = () => {
    textObject.set({
      textAlign: 'center',
    })
    canvas.renderAll()
    pushHistory(canvas)
  }

  // Justify text right
  const textRight = () => {
    textObject.set({
      textAlign: 'right',
    })
    canvas.renderAll()
    pushHistory(canvas)
  }

  return {
    textFontFamily,
    textBold,
    textUnderline,
    textStrikeThrough,
    textItalic,
    textLeft,
    textCenter,
    textRight,
  }
}

// Generic tools for existing objects
export function useCanvasObjectTools(
  canvas,
  activeSelection,
  targetObject,
  pushHistory,
  setStrokeColorValue,
  setFillColorValue,
  setStrokeWidth,
  setActiveSelection,
) {
  const selectStrokeColor = (value) => {
    setStrokeColorValue(value)
    targetObject.set({
      stroke: value,
    })
    canvas.renderAll()
  }

  const selectFillColor = (value) => {
    setFillColorValue(value)
    targetObject.set({
      fill: value,
    })
    canvas.renderAll()
  }

  const handleStrokeWidthChange = (e) => {
    const parsed = parseInt(e.target.value)
    const width = parsed || 1

    setStrokeWidth(width)
    targetObject.set({
      strokeWidth: width,
    })
    canvas.renderAll()
    pushHistory(canvas)
  }

  const sendForward = () => {
    // bringObjectForward seems buggy, so we use bringObjectToFront instead.
    // TODO: Further investigate bringObjectForward and if it's a fabric bug.
    activeSelection.forEach((obj) => canvas.bringObjectToFront(obj))

    canvas.discardActiveObject()
    canvas.renderAll()

    setActiveSelection([])
    pushHistory(canvas)
  }

  const sendBackward = () => {
    activeSelection.forEach((obj) => canvas.sendObjectBackwards(obj))
    canvas.discardActiveObject()
    canvas.renderAll()

    setActiveSelection([])
    pushHistory(canvas)
  }

  const resetRotation = () => {
    targetObject.set({
      angle: 0,
    })
    targetObject.setCoords()
    canvas.renderAll()
    pushHistory(canvas)
  }

  const deleteObj = () => {
    canvas.discardActiveObject()
    activeSelection.forEach((obj) => canvas.remove(obj))
    canvas.requestRenderAll()

    setActiveSelection([])
    pushHistory(canvas)
  }

  return {
    sendForward,
    sendBackward,
    resetRotation,
    deleteObj,
    selectStrokeColor,
    selectFillColor,
    handleStrokeWidthChange,
  }
}
